import React, { useState, useEffect } from 'react';
import { 
  Autocomplete, 
  TextField, 
  CircularProgress,
  Box
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PublicIcon from '@mui/icons-material/Public';
import { Country, City } from 'country-state-city';

const countryAliases = {
  gb: ['uk', 'u.k.', 'united kingdom', 'great britain', 'britain', 'england', 'scotland', 'wales', 'northern ireland'],
  us: ['usa', 'u.s.', 'u.s.a.', 'united states', 'america'],
  ae: ['uae', 'u.a.e.', 'emirates'],
};

const normalizeCountryCode = (countryCode = '') => {
  const normalized = String(countryCode).trim().toUpperCase();
  if (normalized === 'UK') return 'GB';
  return normalized;
};

const stripCountryFromCityInput = (cityName = '') => (
  String(cityName)
    .replace(/,\s*(uk|u\.k\.|gb|great britain|britain|england|united kingdom)\s*$/i, '')
    .replace(/,\s*(usa|u\.s\.a\.|us|united states|america)\s*$/i, '')
    .trim()
);

// API for geocoding if city is not in predefined list
const geocodeLocation = async (locationName, countryCode = '') => {
  try {
    // Add a delay to avoid rate limiting
    await new Promise(resolve => setTimeout(resolve, 500));
    
    // Use a free geocoding service like Nominatim (OpenStreetMap)
    const query = encodeURIComponent(`${locationName}${countryCode ? ', ' + countryCode : ''}`);
    const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&addressdetails=1&accept-language=en`);
    const data = await response.json();
    
    if (data && data.length > 0) {
      console.log('Geocoding result for', locationName, ':', data[0]);
      return {
        coordinates: [parseFloat(data[0].lat), parseFloat(data[0].lon)],
        name: locationName
      };
    }
    
    // If not found, try with just the city name without country code
    if (countryCode) {
      console.log('Trying geocoding without country code for', locationName);
      const fallbackQuery = encodeURIComponent(locationName);
      const fallbackResponse = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${fallbackQuery}&limit=1&addressdetails=1&accept-language=en`);
      const fallbackData = await fallbackResponse.json();
      
      if (fallbackData && fallbackData.length > 0) {
        console.log('Fallback geocoding result for', locationName, ':', fallbackData[0]);
        return {
          coordinates: [parseFloat(fallbackData[0].lat), parseFloat(fallbackData[0].lon)],
          name: locationName
        };
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error geocoding location:', error);
    return null;
  }
};

// Список крупных городов с координатами
// Этот список синхронизирован с citiesGeocoding в ContactMapDialog.js
const majorCities = {
  // North America
  "new york": [40.7128, -74.0060],
  "los angeles": [34.0522, -118.2437],
  "chicago": [41.8781, -87.6298],
  "toronto": [43.6532, -79.3832],
  "mexico city": [19.4326, -99.1332],
  
  // Europe
  "london": [51.5074, -0.1278],
  "paris": [48.8566, 2.3522],
  "berlin": [52.5200, 13.4050],
  "madrid": [40.4168, -3.7038],
  "rome": [41.9028, 12.4964],
  "amsterdam": [52.3676, 4.9041],
  "moscow": [55.7558, 37.6173],
  "saint petersburg": [59.9311, 30.3609],
  "kiev": [50.4501, 30.5234],
  
  // Asia
  "tokyo": [35.6762, 139.6503],
  "beijing": [39.9042, 116.4074],
  "shanghai": [31.2304, 121.4737],
  "hong kong": [22.3193, 114.1694],
  "singapore": [1.3521, 103.8198],
  "mumbai": [19.0760, 72.8777],
  "delhi": [28.6139, 77.2090],
  "tashkent": [41.2995, 69.2401],
  "dushanbe": [38.5598, 68.7870],
  
  // Australia & Oceania
  "sydney": [-33.8688, 151.2093],
  "melbourne": [-37.8136, 144.9631],
  "brisbane": [-27.4698, 153.0251],
  "auckland": [-36.8509, 174.7645],
  
  // South America
  "sao paulo": [-23.5505, -46.6333],
  "rio de janeiro": [-22.9068, -43.1729],
  "buenos aires": [-34.6037, -58.3816],
  "santiago": [-33.4489, -70.6693],
  
  // Africa
  "cairo": [30.0444, 31.2357],
  "lagos": [6.5244, 3.3792],
  "johannesburg": [-26.2041, 28.0473],
  "cape town": [-33.9249, 18.4241],
};

// Find coordinates for a city
const getCityCoordinates = async (cityName, countryCode) => {
  if (!cityName || !countryCode) {
    return null;
  }

  const searchableCityName = stripCountryFromCityInput(cityName);
  const normalizedCountryCode = normalizeCountryCode(countryCode);

  // Нормализуем название города для поиска
  const normalizedCityName = searchableCityName.toLowerCase()
    .trim()
    .replace(/saint/g, 'st')
    .replace(/\./g, '')  // Remove periods
    .replace(/,/g, '')   // Remove commas
    .replace(/-/g, ' '); // Replace hyphens with spaces

  // Проверяем, есть ли город в списке крупных городов
  if (majorCities[normalizedCityName]) {
    return majorCities[normalizedCityName];
  }

  // Проверяем частичное совпадение в списке крупных городов
  const majorCityKey = Object.keys(majorCities).find(key =>
    normalizedCityName.includes(key) || key.includes(normalizedCityName)
  );
  
  if (majorCityKey) {
    return majorCities[majorCityKey];
  }

  // Try to find the city in the CSC library
  const citiesInCountry = City.getCitiesOfCountry(normalizedCountryCode);
  
  if (citiesInCountry && citiesInCountry.length > 0) {
    // Try exact match first (case insensitive)
    const exactMatch = citiesInCountry.find(
      city => city.name.toLowerCase() === searchableCityName.toLowerCase()
    );
    
    if (exactMatch && exactMatch.latitude && exactMatch.longitude) {
      return [parseFloat(exactMatch.latitude), parseFloat(exactMatch.longitude)];
    }
    
    // If no exact match, try partial match - starts with
    const startsWithMatch = citiesInCountry.find(
      city => city.name.toLowerCase().startsWith(searchableCityName.toLowerCase())
    );
    
    if (startsWithMatch && startsWithMatch.latitude && startsWithMatch.longitude) {
      return [parseFloat(startsWithMatch.latitude), parseFloat(startsWithMatch.longitude)];
    }
    
    // If no starts-with match, try partial match - includes
    const partialMatch = citiesInCountry.find(
      city => city.name.toLowerCase().includes(searchableCityName.toLowerCase()) ||
              searchableCityName.toLowerCase().includes(city.name.toLowerCase())
    );
    
    if (partialMatch && partialMatch.latitude && partialMatch.longitude) {
      return [parseFloat(partialMatch.latitude), parseFloat(partialMatch.longitude)];
    }
  }
  
  // If not found in CSC, try geocoding
  const geocodeResult = await geocodeLocation(searchableCityName, normalizedCountryCode);
  if (geocodeResult) {
    return geocodeResult.coordinates;
  }
  
  // Return null if no coordinates found
  return null;
};

const AddressAutocompleteField = ({ 
  type, // 'country' or 'city'
  value,
  onChange,
  selectedCountry = null, // Required for city selection
  label,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  t = {} // translations
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  
  // Normalize string for better matching (handles Turkish characters, accents, etc.)
  const normalizeString = (str) => {
    return str.toLowerCase()
      .normalize('NFD') // Decompose characters with diacritics
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/ı/g, 'i') // Turkish dotless i
      .replace(/İ/g, 'i') // Turkish dotted I
      .replace(/ğ/g, 'g') // Turkish g
      .replace(/Ğ/g, 'g')
      .replace(/ü/g, 'u') // Turkish u
      .replace(/Ü/g, 'u')
      .replace(/ş/g, 's') // Turkish s
      .replace(/Ş/g, 's')
      .replace(/ö/g, 'o') // Turkish o
      .replace(/Ö/g, 'o')
      .replace(/ç/g, 'c') // Turkish c
      .replace(/Ç/g, 'c')
      .trim();
  };

  useEffect(() => {
    if (type === 'country') {
      // For country type, get countries from the library
      const countryList = Country.getAllCountries().map(country => ({
        name: country.name,
        code: country.isoCode,
        flag: country.flag,
        phoneCode: country.phonecode
      }));
      setOptions(countryList);
    } else if (type === 'city' && selectedCountry && inputValue.length >= 3) {
      // For city type, get cities from the library only if at least 3 characters are entered
      const countryCode = normalizeCountryCode(typeof selectedCountry === 'object' ? selectedCountry.code : selectedCountry);
      
      if (countryCode) {
        setLoading(true);
        const cityList = City.getCitiesOfCountry(countryCode);
        if (cityList && cityList.length > 0) {
          const normalizedInput = normalizeString(stripCountryFromCityInput(inputValue));
          
          // Transform the cities data to match our format
          const formattedCities = cityList
            .filter(city => {
              // Use normalized matching for better results
              const normalizedCityName = normalizeString(city.name);
              return normalizedCityName.includes(normalizedInput);
            })
            .map(city => ({
              name: city.name,
              coordinates: city.latitude && city.longitude ? [parseFloat(city.latitude), parseFloat(city.longitude)] : null
            }))
            // Include cities even without coordinates (they can be geocoded later)
            .slice(0, 50); // Limit to 50 results for better performance
          
          // If no cities found in library, allow manual entry (will be geocoded)
          if (formattedCities.length === 0) {
            // Don't set empty - let user type and we'll geocode it
            setOptions([]);
          } else {
            setOptions(formattedCities);
          }
        } else {
          setOptions([]);
        }
        setLoading(false);
      }
    } else if (type === 'city' && selectedCountry && inputValue.length < 3) {
      // If less than 3 characters, show a limited set or empty
      setOptions([]);
    }
  }, [type, selectedCountry, inputValue]);
  
  useEffect(() => {
    // Initialize the input value when the value prop changes
    if (value) {
      if (typeof value === 'string') {
        setInputValue(value);
      } else if (value.name) {
        setInputValue(value.name);
      }
    } else {
      setInputValue('');
    }
  }, [value]);
  
  const handleChange = async (event, newValue) => {
    
    if (!newValue) {
      onChange(null);
      return;
    }
    
    if (type === 'country') {
      onChange(newValue);
    } else if (type === 'city') {
      setLoading(true);
      
      try {
        // If simple string was entered instead of selecting from dropdown
        if (typeof newValue === 'string') {
          // Only process if we have at least 3 characters
          if (newValue.length < 3) {
            setLoading(false);
            return;
          }
          
          const countryCode = normalizeCountryCode(typeof selectedCountry === 'object' ? selectedCountry.code : selectedCountry);
          const cityName = stripCountryFromCityInput(newValue);
          
          // Попробуем найти координаты для города
          const coordinates = await getCityCoordinates(cityName, countryCode);
          
          // Даже если координаты не найдены, все равно сохраняем город
          const cityData = {
            name: cityName,
            coordinates: coordinates || null
          };
          
          // Логируем результат для отладки
          console.log(`City data for ${cityName}:`, cityData);
          
          onChange(cityData);
          
          // Если координаты не найдены, показываем предупреждение в консоли
          if (!coordinates) {
            console.warn(`Coordinates not found for city: ${cityName}. City will be saved without coordinates.`);
          }
        } else {
          // If selected from dropdown, we already have the coordinates
          onChange(newValue);
        }
      } catch (error) {
        console.error('Error processing city selection:', error);
        
        // В случае ошибки все равно сохраняем город без координат
        if (typeof newValue === 'string' && newValue.length >= 3) {
          const cityData = {
            name: newValue,
            coordinates: null
          };
          onChange(cityData);
          console.warn(`Error finding coordinates for ${newValue}. City will be saved without coordinates.`);
        }
      } finally {
        setLoading(false);
      }
    }
  };
  
  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
    
    // If it's a city field and we have at least 3 characters, show loading indicator
    if (type === 'city' && newInputValue.length >= 3) {
      setLoading(true);
    } else if (type === 'city') {
      // If less than 3 characters for city, clear options
      setOptions([]);
      setLoading(false);
    }
  };

  // For filtering options based on input
  const filterOptions = (options, { inputValue }) => {
    if (!inputValue || inputValue.length < 3) {
      if (type !== 'country') return [];

      const shortInput = normalizeString(inputValue || '');
      if (shortInput.length < 2) return options;

      return options.filter((option) => {
        const code = normalizeString(option.code || '');
        const aliases = countryAliases[(option.code || '').toLowerCase()] || [];
        return code === shortInput || aliases.some((alias) => normalizeString(alias) === shortInput);
      });
    }
    
    const normalizedInput = normalizeString(type === 'city' ? stripCountryFromCityInput(inputValue) : inputValue);
    
    // Enhanced filtering by name with better matching (using normalized strings)
    return options.filter(option => {
      const optionName = typeof option === 'object' ? option.name : option;
      const normalizedName = normalizeString(optionName);
      const normalizedCode = typeof option === 'object' ? normalizeString(option.code || '') : '';
      const aliases = typeof option === 'object' ? (countryAliases[(option.code || '').toLowerCase()] || []) : [];
      const normalizedAliases = aliases.map(normalizeString);
      
      if (type === 'country' && (
        normalizedCode === normalizedInput ||
        normalizedAliases.some((alias) => alias.includes(normalizedInput) || normalizedInput.includes(alias))
      )) {
        return true;
      }
      
      // First try exact match (case insensitive, normalized)
      if (normalizedName === normalizedInput) {
        return true;
      }
      
      // Then try starts with
      if (normalizedName.startsWith(normalizedInput)) {
        return true;
      }
      
      // Finally try includes
      return normalizedName.includes(normalizedInput);
    }).sort((a, b) => {
      // Sort by relevance - exact match first, then starts with, then includes
      const nameA = typeof a === 'object' ? normalizeString(a.name) : normalizeString(a);
      const nameB = typeof b === 'object' ? normalizeString(b.name) : normalizeString(b);
      const codeA = typeof a === 'object' ? normalizeString(a.code || '') : '';
      const codeB = typeof b === 'object' ? normalizeString(b.code || '') : '';
      const aliasesA = typeof a === 'object' ? (countryAliases[(a.code || '').toLowerCase()] || []).map(normalizeString) : [];
      const aliasesB = typeof b === 'object' ? (countryAliases[(b.code || '').toLowerCase()] || []).map(normalizeString) : [];
      const aAliasMatch = codeA === normalizedInput || aliasesA.includes(normalizedInput);
      const bAliasMatch = codeB === normalizedInput || aliasesB.includes(normalizedInput);
      
      if (aAliasMatch && !bAliasMatch) return -1;
      if (!aAliasMatch && bAliasMatch) return 1;
      
      // Exact match gets highest priority
      if (nameA === normalizedInput && nameB !== normalizedInput) return -1;
      if (nameA !== normalizedInput && nameB === normalizedInput) return 1;
      
      // Starts with gets second priority
      const aStartsWith = nameA.startsWith(normalizedInput);
      const bStartsWith = nameB.startsWith(normalizedInput);
      
      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      
      // Default to alphabetical
      return nameA.localeCompare(nameB);
    }).slice(0, 50); // Limit to 50 results for better performance
  };

  return (
    <Autocomplete
      id={`${type}-autocomplete`}
      options={options}
      getOptionLabel={(option) => {
        // Handle both string and object options
        if (typeof option === 'string') return option;
        return option.name || '';
      }}
      isOptionEqualToValue={(option, value) => {
        // Handle both string and object options
        if (typeof option === 'string' && typeof value === 'string') {
          return option === value;
        }
        if (typeof option === 'object' && typeof value === 'object') {
          return option.code === value.code || option.name === value.name;
        }
        return false;
      }}
      filterOptions={filterOptions}
      value={value}
      onChange={handleChange}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      disabled={disabled || (type === 'city' && !selectedCountry)}
      loading={loading}
      freeSolo={type === 'city'} // Allow custom city input
      clearOnBlur={false}
      selectOnFocus
      handleHomeEndKeys
      openOnFocus={type === 'country' || (type === 'city' && inputValue.length >= 3 && options.length > 0)}
      autoHighlight
      blurOnSelect
      noOptionsText={
        type === 'city' && inputValue.length < 3 
          ? (t.enterAtLeast3Chars || 'Enter at least 3 characters to search') 
          : (t.noOptions || 'No options')
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          error={Boolean(error)}
          helperText={
            type === 'city' && !selectedCountry 
              ? (t.selectCountryFirst || 'Select country first')
              : type === 'city' && inputValue.length < 3 && selectedCountry
                ? (t.enterAtLeast3Chars || 'Enter at least 3 characters to search')
                : helperText
          }
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                {type === 'country' ? <PublicIcon color="action" sx={{ mr: 1 }} /> : <LocationOnIcon color="action" sx={{ mr: 1 }} />}
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => {
        const { key, ...otherProps } = props;
        return (
          <Box component="li" key={key} {...otherProps}>
            {option.name || option}
            {type === 'country' && option.code && (
              <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                ({option.code}) {option.flag}
              </Box>
            )}
            {type === 'city' && option.coordinates && (
              <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontSize: '0.8rem' }}>
                (✓ Coordinates)
              </Box>
            )}
          </Box>
        );
      }}
    />
  );
};

export { getCityCoordinates, majorCities };
export default AddressAutocompleteField; 
